import { useInject } from 'dependable-react';
import { css } from 'emotion';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import closeSvg from '../assets/close.svg';
import { BREAKPOINT_MEDIUM } from '../consts/breakpoints';
import { MoreMagicType } from '../enums/MoreMagicType';
import { APP_DATA_TOKEN, I18N_TOKEN } from '../services/InjectTokens';
import { Avatar, Upload } from '../state/models';
import { Element } from '../state/models/Element';
import { deapify } from '../utils/apify';
import { pushGtmData } from '../utils/gtm';
import { MagicBuilderPersons } from './MagicBuilderPersons';
import { MagicBuilderUpload } from './MagicBuilderUpload';

/*
  FIXME: Make this and Charbuilder.tsx use the same container.
    ~ Andrei
*/

const main = css`
  position: absolute;
  top: 60px;
  left: 0;
  right: 0;

  background: white;
  z-index: 501;
  padding-bottom: 65px;

  @media (max-width: ${BREAKPOINT_MEDIUM}) {
    position: fixed;
    top: 80px;
    bottom: 0;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 0;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 65px;
    background-image: linear-gradient(to bottom, rgba(226, 226, 226, 0), rgba(193, 193, 193, 0.37));

    @media (max-width: ${BREAKPOINT_MEDIUM}) {
      display: none;
      height: 0;
    }
  }
`;

const content = css`
  max-width: 950px;
  margin: 0 auto;
  padding: 20px;
  @media (min-width: ${BREAKPOINT_MEDIUM}) {
    margin: 20px auto 0;
    padding: 0 20px;
  }
`;

const close = css`
  position: absolute;
  top: 10px;
  right: 30px;
  background: url(${closeSvg});
  height: 20px;
  width: 20px;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  cursor: pointer;

  @media (max-width: ${BREAKPOINT_MEDIUM}) {
    display: block;
  }
`;

const heading = css`
  font-size: 14px;
  font-weight: bold;
  color: #414042;
  margin-top: 10px;

  @media (min-width: ${BREAKPOINT_MEDIUM}) {
    margin-top: 0;
  }
`;

interface IProps {
  isVisible: boolean;
  elements: Array<Element>;
  type: string;
  title: string;
  params: Record<string, string>;
  images: {
    childImage?: Upload;
    dedicationImage?: Upload;
  };
  onClose(): void;
  onError(): void;
  onRemovePhoto(imageToRemove: Upload): void;
  onSave(params: Record<string, string>, upload?: Upload): void;
}

export const MagicBuilder = observer(
  ({ elements, onSave, images, onClose, onError, isVisible, type, title, params, onRemovePhoto }: IProps) => {
    const i18n = useInject(I18N_TOKEN);
    const appState = useInject(APP_DATA_TOKEN);
    const avatars = appState.avatars
      .map((avatar) => deapify(avatar.params) as Avatar['params'])
      .reduce((acc, curr) => ({ ...curr, ...acc }), {});

    const avatarDefaults = appState.avatars
      .map((avatar) => deapify(avatar.defaultValues[appState.currentLanguage]) as Avatar['defaultValues'])
      .reduce((acc, curr) => ({ ...curr, ...acc }), {});

    const avatarVariables = {
      ...avatarDefaults,
      ...avatars,
    };

    if (!isVisible) {
      return null;
    }
    pushGtmData({
      event: 'personalizacija',
      korak: { items: [{ step: `step_add_${title}` }] },
    });

    return (
      <div className={main}>
        <div className={content}>
          <div role="button" onClick={onClose} className={close} />
          <div className={heading}>{i18n.t('more_magic')}</div>
          {type !== 'iconed' && (
            <MagicBuilderUpload
              type={type as MoreMagicType}
              onSave={onSave}
              onError={onError}
              onClose={onClose}
              onRemovePhoto={onRemovePhoto}
              params={params}
              elements={elements}
              images={images}
              //@ts-ignore
              dedicationDefault={i18n.t('dedicationDefault', avatarVariables)}
            />
          )}
          {type === 'iconed' && <MagicBuilderPersons params={params} elements={elements} onPersonsSave={onSave} />}
        </div>
      </div>
    );
  },
);
